/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useMemo, useEffect, useRef } from 'react';
import { ACTIONS, useDeviceInfo } from '@airasia-rn/ui';
import NotificationView, {
  NotificationViewStyle,
  ActionTitlePlacement,
} from '@airasia-phoenix/react-native/components/NotificationView';
import { useNotificationData } from '../../hooks/useNotificationData';
import { useLogger } from '@airasia-rn/core-logger';
import { HomepageEvent, HomePageEvents } from '../../analytics/homepage';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { useHomeNavigation } from '../../contexts/HomeNavigationContext';

interface NotificationBannerViewProps {
  onDataLoaded?: () => void;
}

const NotificationBannerView: React.FC<NotificationBannerViewProps> = ({
  onDataLoaded,
}) => {
  const isNullOrEmpty = (value?: string | null) =>
    value == null || value.trim().length === 0;

  useEffect(() => {
    if (onDataLoaded) {
      onDataLoaded();
    }
  }, [onDataLoaded]);

  const { isMobileWidth } = useDeviceInfo();

  const { isOffline } = useNetworkStatus();

  // Resolve the platform inside the hook: forced (web) or detected (native),
  // so the banner shares the homepage cache slot instead of fetching its own.
  const notificationData = useNotificationData();
  const shouldRenderBanner =
    !isOffline && !isNullOrEmpty(notificationData?.text);

  const { logEvent } = useLogger();
  const hasTrackedView = useRef(false);

  const { navigateToNotificationBanner } = useHomeNavigation();

  useEffect(() => {
    if (shouldRenderBanner && notificationData && !hasTrackedView.current) {
      hasTrackedView.current = true;
      logEvent(
        ACTIONS.view,
        HomepageEvent.trackNotificationBannerViewed({
          label: notificationData.text,
        }),
      );
    }
  }, [notificationData]);

  const actionTitlePosition = useMemo(() => {
    return !isMobileWidth
      ? ActionTitlePlacement.RIGHT
      : ActionTitlePlacement.BOTTOM;
  }, [isMobileWidth]);

  const handleActionPress = () => {
    logEvent(
      ACTIONS.click,
      HomepageEvent.trackNotificationBannerClicked({
        label: notificationData?.text ?? '',
      }),
    );
    if (navigateToNotificationBanner) {
      navigateToNotificationBanner(notificationData?.actionUrl);
    } else if (notificationData?.actionUrl) {
      console.log('Navigating to:', notificationData.actionUrl);
      // Add your navigation logic here
    }
  };

  if (!notificationData || !shouldRenderBanner) return null;

  return (
    <NotificationView
      text={notificationData.text}
      style={NotificationViewStyle.WARNING}
      actionTitle={notificationData.actionTitle}
      actionTitlePosition={actionTitlePosition}
      actionOnPress={handleActionPress}
      width="100%"
    />
  );
};

export default NotificationBannerView;
